import { useMemo, useState } from "react";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const getMethodColor = (templateName) => {
    const methodColors = {
        "Claims HUT": "#FFEBF8",
        "Blind benchmarking": "#FFEBF8",
        "Product Improvement": "#FFEBF8",
        "Superiority": "#FFEBF8",
        "Early Screening": "#FFEBF8",
        "ICT": "#FFFBC3",
        "CLT": "#E3C4FF",
        "Express CLT": "#FFDDAA",
        "Concept Testing": "#FFDDAA",
    };
    return methodColors[templateName] || '#e0e0e0';
};


export const getStudyTypeColor = (studyType) => {
    if (studyType === "Home Use Test") return "#005eef";
    if (studyType === "Fragrance Testing") return "#4CAF50";
    return "#333";
};

export const transformStudyTypeOptions = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((type) => ({
        value: type.id,
        label: type.title,
    }));
};

export const transformStudyTypeMethodOptions = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((method) => ({
        value: method.id,
        label: method.title,
        originalLabel: method.title,
        studyTypeLabel: method.study_type_title,
        studyTypeValue: method.study_type,
        template: method.default_title,
        color: getMethodColor(method.default_title),
    }));
};

export const getInfoMessageOption = (message = 'Study Type Methods sharing a same template will automatically be selected together') => {
    return {
        value: "info-message",
        label: (
            <div style={{ display: "flex", alignItems: "center" }}>
                <InfoOutlinedIcon style={{ color: "#ffc107", marginRight: "8px", fontSize: "18px" }} />
                <span style={{ color: "#000000" }}>
                    {message}
                </span>
            </div>
        ),
        isDisabled: true,
    };
};

export const handleTemplateBasedMethodSelection = (currentSelection, actionMeta, allOptions) => {
    if (!actionMeta) return currentSelection;

    if (actionMeta.action === "select-option") {
        const selectedTemplate = actionMeta.option.template;
        
        // Find all methods with the same template
        const methodsForTemplate = allOptions.filter(
            (opt) => opt.template === selectedTemplate
        );
        
        // Merge with existing selection, avoiding duplicates
        const selectionMap = new Map();
        [...currentSelection, ...methodsForTemplate].forEach((item) => {
            selectionMap.set(item.value, item);
        });
        
        return Array.from(selectionMap.values());
    } 
    else if (actionMeta.action === "remove-value") {
        const templateToRemove = actionMeta.removedValue?.template;
        
        return currentSelection.filter(
            (opt) => opt.template !== templateToRemove
        );
    } 
    else if (actionMeta.action === "clear") {
        return [];
    }
    
    return currentSelection;
};

export const getStudyTypeMethodSelectStyles = (baseStyles = {}) => ({
    ...baseStyles,
    control: (base, state) => ({
        ...base,
        minHeight: "30px",
        height: state.hasValue ? "auto" : "30px",
        alignItems: state.hasValue ? "flex-start" : "center",
        padding: 0,
        margin: 0,
        border: state.isDisabled ? "1px solid #ccc" : "1px solid #1976d2cc",
    }),
    valueContainer: (base, state) => ({
        ...base,
        padding: "0px 6px",
        margin: "0px",
        marginBottom: state.selectProps.value?.length > 0 ? "10px" : "0px",
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "4px",
        maxHeight: "70px",
        overflowY: "auto",
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: "10px",
    }),
    multiValue: (base, { data }) => ({
        ...base,
        backgroundColor: data.color,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#000",
        fontWeight: 300,
        fontSize: "9px",
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#000",
        ":hover": {
            backgroundColor: "rgba(0,0,0,0.1)",
        },
    }),
});

export const formatStudyTypeLabel = ({ label }) => {
    return (
        <span style={{ color: getStudyTypeColor(label) }}>
            {label}
        </span>
    );
};

export const formatMethodLabel = (option) => {
    const { label, studyTypeLabel, value, color } = option;
    
    if (value === "info-message") {
        return label;
    }
    
    return (
        <div
            style={{
                display: "flex",
                alignItems: "flex-start",
                backgroundColor: color,
                padding: "4px 4px",
                borderRadius: "4px",
                color: "#000",
            }}
        >
            <span>{label}</span>
            <span
                style={{
                    color: getStudyTypeColor(studyTypeLabel),
                    fontSize: "10px",
                    marginLeft: "4px",
                }}
            >
                ({studyTypeLabel})
            </span>
        </div>
    );
};

export const useStudyTypeFilter = (dispatch, loadStudyTypeAction, loadStudyTypeMethodAction) => {
    const [studyTypeOptions, setStudyTypeOptions] = useState([]);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState([]);
    const [selectedStudyType, setSelectedStudyType] = useState(null);
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState([]);

    const optionsWithInfoMessage = useMemo(() => {
        if (studyTypeMethodOptions.length === 0) return [];
        const infoMessage = getInfoMessageOption();
        return [infoMessage, ...studyTypeMethodOptions];
    }, [studyTypeMethodOptions]);

    const customMethodReactSelectStyles = useMemo(
        () => getStudyTypeMethodSelectStyles(),
        []
    );

    const handleStudyTypeChange = (selectedValue) => {
        setSelectedStudyType(selectedValue);
        if (dispatch && loadStudyTypeAction) {
            dispatch(loadStudyTypeAction(selectedValue));
        }
        if (selectedValue && (!selectedStudyTypeMethod.length || selectedStudyTypeMethod[0]?.studyTypeValue !== selectedValue.value)) {
            setSelectedStudyTypeMethod([]);
            if (dispatch && loadStudyTypeMethodAction) {
                dispatch(loadStudyTypeMethodAction([]));
            }
        }
    };

    const handleStudyTypeMethodChange = (newValue, actionMeta, allOptions) => {
        const updatedSelection = handleTemplateBasedMethodSelection(
            selectedStudyTypeMethod,
            actionMeta,
            allOptions || studyTypeMethodOptions
        );
        setSelectedStudyTypeMethod(updatedSelection);
        if (dispatch && loadStudyTypeMethodAction) {
            dispatch(loadStudyTypeMethodAction(updatedSelection));
        }
    };

    const resetFilters = () => {
        setSelectedStudyType(null);
        setSelectedStudyTypeMethod([]);
        if (dispatch && loadStudyTypeAction) {
            dispatch(loadStudyTypeAction(null));
        }
        if (dispatch && loadStudyTypeMethodAction) {
            dispatch(loadStudyTypeMethodAction([]));
        }
    };

    return {
        studyTypeOptions,
        setStudyTypeOptions,
        studyTypeMethodOptions,
        setStudyTypeMethodOptions,
        selectedStudyType,
        selectedStudyTypeMethod,
        optionsWithInfoMessage,
        customMethodReactSelectStyles,
        handleStudyTypeChange,
        handleStudyTypeMethodChange,
        resetFilters,
    };
};